import { useState } from 'react'

import { getOnlyDate } from '~/astra'
import { createBatchPractitionerScheduleLocation, deleteBatchPractitionerScheduleLocation } from '~/store/di-entity.api'
import { createMinimalLocationScheduleId } from '~/store/slices/surgeonSchedulerSlice'
import { useStore } from '~/store/store'
import { Location, PractitionerScheduleLocation, selectPractitionerScheduleLocation } from '~/storm'
import { trackUserEvent } from '~/utils/analytics'

import { autoAssignStatuses, DatePractitionerCell } from '../../../shared'

export function createGetLocationStateFn(selectedCells: DatePractitionerCell[], scheduleLocations: Pick<PractitionerScheduleLocation, 'location_id'>[]) {
    return (locationId: Location['id']) => {
        const numCellsWithLocation = scheduleLocations.filter(location => location.location_id === locationId).length
        return numCellsWithLocation === 0 ? 'none' : numCellsWithLocation === selectedCells.length ? 'all' : 'some'
    }
}

export const useToggleLocationSchedules = (selectedCells: DatePractitionerCell[], scheduleLocations: PractitionerScheduleLocation[]) => {
    const [isLoading, setIsLoading] = useState(false)

    const addBatchPendingLocationSchedule = useStore(state => state.surgeonScheduler.actions.addBatchPendingLocationSchedule)
    const deleteBatchPendingLocationSchedule = useStore(state => state.surgeonScheduler.actions.deleteBatchPendingLocationSchedule)
    const getLocationState = createGetLocationStateFn(selectedCells, scheduleLocations)

    async function saveLocationSchedules(locationId: Location['id']) {
        const state = useStore.getState()
        const cellsWithoutLocation = selectedCells.filter(
            ({ date, practitionerId }) =>
                !selectPractitionerScheduleLocation(state, {
                    where: { scheduled_date: getOnlyDate(date), practitioner_id: practitionerId, location_id: locationId },
                })
        )

        const locationsToCreate = cellsWithoutLocation.map(({ date, practitionerId }) => ({
            practitioner_id: practitionerId,
            scheduled_date: getOnlyDate(date),
            location_id: locationId,
        }))

        if (locationsToCreate.length) {
            await createBatchPractitionerScheduleLocation(locationsToCreate)
        }

        return locationsToCreate.length
    }

    async function deleteLocationSchedules(locationId: Location['id']) {
        const locationSchedulesToDelete = scheduleLocations.filter(scheduleLocation => scheduleLocation.location_id === locationId).map(location => location.id)

        try {
            await deleteBatchPractitionerScheduleLocation(locationSchedulesToDelete)
        } catch (error) {
            console.error(error)
            return -1
        }
        return locationSchedulesToDelete.length
    }

    function addPendingLocations(location: Location) {
        const state = useStore.getState()

        const selectedCellsWithoutLocation = selectedCells.filter(
            ({ date, practitionerId }) =>
                !selectPractitionerScheduleLocation(state, {
                    where: { scheduled_date: getOnlyDate(date), location_id: location.id, practitioner_id: practitionerId },
                })
        )

        const locationsToAdd = selectedCellsWithoutLocation.map(({ date, practitionerId }) => ({
            date: getOnlyDate(date),
            practitionerId,
            room_code: location.room_code,
            location_id: location.id,
        }))
        addBatchPendingLocationSchedule(locationsToAdd)
    }

    function deletePendingLocations(location: Location) {
        const ids = selectedCells.map(({ date, practitionerId }) =>
            createMinimalLocationScheduleId({
                date: getOnlyDate(date),
                practitionerId,
                room_code: location.room_code,
            })
        )
        deleteBatchPendingLocationSchedule(ids)
    }

    async function toggleLocationSchedules(location: Location) {
        const state = getLocationState(location.id)
        const rooms = [location.room_code].filter(Boolean)

        setIsLoading(true)

        if (state === 'none' || state === 'some') {
            addPendingLocations(location)

            const [numberOfEdits] = await Promise.all([
                saveLocationSchedules(location.id),
                autoAssignStatuses(selectedCells.map(c => ({ ...c, locationId: location.id }))),
            ])
            void trackUserEvent('SURGEON_LOCATION_ADDED', { numberOfEdits, rooms })
            deletePendingLocations(location)
        }

        if (state === 'all') {
            const numberOfEdits = await deleteLocationSchedules(location.id)
            void trackUserEvent('SURGEON_LOCATION_REMOVED', { numberOfEdits, rooms })
        }

        setIsLoading(false)
    }

    return { isLoading, toggleLocationSchedules }
}
